import axios from 'axios';

// Get base URL from environment variable
const API_URL = process.env.REACT_APP_API_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Cache-Control': 'no-cache'
  },
  withCredentials: true
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      const basePath = process.env.PUBLIC_URL || '';
      window.location.href = `${basePath}/login`;
    }
    return Promise.reject(error);
  }
);

// Auth service
export const authService = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getProfile: () => api.get('/auth/me')
};

// User service
export const userService = {
  getUsers: (companyId) => api.get('/users', { params: companyId && companyId !== 'all' ? { companyId } : {} }),
  createUser: (userData) => api.post('/users', userData),
  updateUser: (id, userData) => api.put(`/users/${id}`, userData),
  updateProfile: (userData) => api.put('/users/me', userData),
  deleteUser: (id) => api.delete(`/users/${id}`)
};

// Company service
export const companyService = {
  getCompanies: () => api.get('/companies'),
  getMyCompanies: () => api.get('/companies/my-companies'),
  getCompany: (id) => api.get(`/companies/${id}`),
  createCompany: (companyData) => api.post('/companies', companyData),
  updateCompany: (id, companyData) => api.put(`/companies/${id}`, companyData),
  deleteCompany: (id) => api.delete(`/companies/${id}`)
};

// KPI service
export const kpiService = {
  getDashboard: (companyId) => api.get('/kpi/dashboard', { params: companyId && companyId !== 'all' ? { companyId } : {} }),
  getMyKPIs: () => api.get('/kpi/my'),
  getKPIById: (id) => api.get(`/kpi/${id}`),
  createKPI: (kpiData) => api.post('/kpi', kpiData),
  updateKPI: (id, kpiData) => api.put(`/kpi/${id}`, kpiData),
  deleteKPI: (id) => api.delete(`/kpi/${id}`),
  assignKPI: (id, data) => api.patch(`/kpi/${id}/assign`, data),
  updateTask: (kpiId, areaId, taskId, data) => api.patch(`/kpi/${kpiId}/areas/${areaId}/tasks/${taskId}`, data),
  getCompanyStats: (companyId) => api.get(`/kpi/stats/company/${companyId}`),
};

// Task services
export const taskService = {
  getAreaTasks: (areaId) => api.get(`/tasks/area/${areaId}`),
  getMyTasks: () => api.get('/tasks/my-tasks'),
  createTask: (taskData) => api.post('/tasks', taskData),
  updateTask: (id, taskData) => api.put(`/tasks/${id}`, taskData),
  deleteTask: (id) => api.delete(`/tasks/${id}`),
};

// API service
export const apiService = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getProfile: () => api.get('/auth/me'),
  getCompanies: () => api.get('/companies'),
  createCompany: (companyData) => api.post('/companies', companyData),
  updateCompany: (id, companyData) => api.put(`/companies/${id}`, companyData),
  deleteCompany: (id) => api.delete(`/companies/${id}`),
  getUsers: () => api.get('/users'),
  createUser: (userData) => api.post('/users', userData),
  updateUser: (id, userData) => api.put(`/users/${id}`, userData),
  deleteUser: (id) => api.delete(`/users/${id}`),
  getAreaTasks: (areaId) => api.get(`/tasks/area/${areaId}`),
  getMyTasks: () => api.get('/tasks/my-tasks'),
  createTask: (taskData) => api.post('/tasks', taskData),
  updateTask: (id, taskData) => api.put(`/tasks/${id}`, taskData),
  deleteTask: (id) => api.delete(`/tasks/${id}`),
};

export default api;
